/**
 * Weights on the WHO growth curves, for the phone. The WHO table lives at
 * `@babia/checks/growth` (about 12 KB gzipped), and this is the one file of
 * the SPA that imports it: lazily, so the log screen downloads it only once
 * it shows a weight with a number (`tests/weights.test.ts` keeps it so).
 * The words here describe a weight, never judge it.
 */
import {
  birthDateText,
  type GrowthBaby,
  growthAge,
  localCalendar,
  type NoGrowthReason,
  percentileText,
} from "@babia/checks";
import type * as GrowthModule from "@babia/checks/growth";
import { useEffect, useState } from "react";

export type Growth = typeof GrowthModule;

let loaded: Growth | null = null;
let loading: Promise<Growth> | null = null;

/** The growth module, loaded once; a failed load is tried again next time. */
export function loadGrowth(): Promise<Growth> {
  loading ??= import("@babia/checks/growth").then(
    (module) => (loaded = module),
    (error: unknown) => {
      loading = null;
      throw error;
    },
  );
  return loading;
}

/**
 * The growth module once `wanted` and loaded (null until then), and whether
 * the load failed: the weight itself still shows, with no percentile.
 */
export function useGrowth(wanted: boolean): { growth: Growth | null; failed: boolean } {
  const [growth, setGrowth] = useState<Growth | null>(loaded);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!wanted || growth) return;
    let live = true;
    loadGrowth().then(
      (module) => {
        if (live) setGrowth(module);
      },
      () => {
        if (live) setFailed(true);
      },
    );
    return () => {
      live = false;
    };
  }, [wanted, growth]);
  return { growth, failed };
}

/** "38th percentile", with "(corrected age)" for a baby born early; null without one. */
export function percentileWords(
  growth: Growth | null,
  baby: GrowthBaby,
  grams: number,
  at: Date,
): string | null {
  if (!growth) return null;
  const read = growth.weightReading(baby, grams, localCalendar.isoDate(at));
  if ("reason" in read) return null;
  return `${percentileText(read.percentile)}${read.corrected ? " (corrected age)" : ""}`;
}

/** Why a weight gets no percentile, said for the parent, with where to fix it. */
export function reasonWords(reason: NoGrowthReason, name: string, dueDate: string | null): string {
  switch (reason) {
    case "no-weight":
      return "This weight is not a number Babia can read.";
    case "no-sex":
      return `Pick Girl or Boy for ${name} in Settings to see percentiles: the WHO curves differ.`;
    case "no-birth-date":
      return `Add ${name}'s birthday in Settings to see percentiles.`;
    case "before-birth":
      return `This weight is dated before ${name}'s birthday.`;
    case "before-due-date":
      return `Percentiles start on the due date${dueDate ? `, ${birthDateText(dueDate)}` : ""}: the WHO curves begin at term.`;
    case "past-two":
      return "Babia shows WHO percentiles to age 2.";
  }
}

/** Why a weight logged today would get no percentile, or null when it would get one. */
export function todayReason(baby: GrowthBaby, today: string): NoGrowthReason | null {
  const age = growthAge(baby, today);
  return "reason" in age ? age.reason : null;
}

/** For a baby with no due date, which reads as born on time. */
export const PRETERM_HINT = "Born more than 3 weeks early? Add the due date.";
